import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EMPTY_DISPLAY_VALUE } from '@shared/constants/display';

dayjs.extend(utc); // Extiende dayjs con el plugin UTC para manejar fechas en UTC

const GENERAL_VALUE_LABELS_MAP = new Map([
  [true, 'Sí'],
  [false, 'No'],
  ['si', 'Sí'],
  ['no', 'No'],
]);

const formatPersonSummary = (value) => {
  if (typeof value !== 'object' || value === null) return value;

  const fullNameParts = [value.first_name, value.last_name];
  const fullName = fullNameParts.filter(Boolean).join(' ');

  const documentParts = [value.document_type, value.document_number];
  const document = documentParts.filter(Boolean).join(' ');

  const summaryParts = [fullName, document];
  const summary = summaryParts.filter(Boolean).join(' | ');

  if (!summary) {
    return value.id != null ? `(ID ${value.id})` : null;
  }

  return summary;
};

const FIELD_LABELS = {
  client: {
    id: 'ID',
    first_name: 'Nombres',
    last_name: 'Apellidos',
    document_type: {
      label: 'Tipo de documento',
      values: {
        cc: 'Cédula de ciudadanía (CC)',
        ce: 'Cédula de extranjería (CE)',
        ti: 'Tarjeta de identidad (TI)',
        nit: 'NIT',
        pa: 'Pasaporte (PA)',
      },
    },
    document_number: 'Número de documento',
    birthday_date: 'Fecha de nacimiento',
    gender: {
      label: 'Género',
      values: {
        masculino: 'Masculino',
        femenino: 'Femenino',
        empresa: 'Empresa',
      },
    },
    email: 'Correo electrónico',
    phone_number: 'Número de celular',
    softseguros_id: 'Softseguros ID',
  },
  vehicle: {
    id: 'ID',
    placa: 'Placa',
    marca: 'Marca',
    modelo: 'Modelo',
    referencia: 'Referencia',
    clase: 'Clase',
    combustible: 'Combustible',
    codigo_fasecolda: 'Código Fasecolda',
    valor_asegurado: 'Valor asegurado',
    valor_accesorios: 'Valor accesorios',
    ciudad_circulacion: { hidden: true }, // No se muestra el ID de la ciudad de circulación
    ciudad_circulacion_name: 'Ciudad de circulación',
    servicio: {
      label: 'Tipo de servicio',
      values: {
        particular: 'Particular',
        publico: 'Público',
      },
    },
    is_utilitarian: 'Utilitario',
    is_new: 'Nuevo',
    has_plate: 'Tiene placa',
  },
  quotation: {
    id: 'ID',
    state: {
      label: 'Estado',
      values: {
        data_gateway: 'En pasarela de datos', // Estado obsoleto, se conserva por compatibilidad con logs históricos
        pending: 'Pendiente por cotizar',
        done: 'Cotización generada',
        slope_emit: 'Pendiente de emisión',
        emitted: 'Póliza emitida',
      },
    },
    number: 'Número de cotización',
    beneficiary_same_as_client: 'El beneficiario es el mismo tomador',
    beneficiary: {
      label: 'Beneficiario',
      formatter: formatPersonSummary,
    },
    sede: { hidden: true }, // No se muestra el ID de la sede
    sede_name: 'Sede',
    client_data: {
      label: 'Tomador',
      formatter: formatPersonSummary,
    },
    vehicle_data: {
      label: 'Vehículo',
      formatter: (value) => {
        if (typeof value !== 'object' || value === null) return value;

        const summaryParts = [value.placa, value.marca, value.modelo]
        const summary = summaryParts.filter(Boolean).join(' | ');

        if (!summary) {
          return value.id != null ? `(ID ${value.id})` : null;
        }

        return summary;
      }
    },
  },
}

const getFieldLabel = (entity, field) => {
  const fieldInfo = FIELD_LABELS[entity]?.[field];

  if (fieldInfo?.hidden) return null; // Si el campo está marcado como hidden, no se muestra en el historial de cambios

  return typeof fieldInfo === 'string'
    ? fieldInfo
    : (fieldInfo?.label ?? field);
};

const getValueLabel = (entity, field, value) => {
  const cleanedValue = typeof value === 'string' ? value.trim() : value;
  const fieldInfo = FIELD_LABELS[entity]?.[field];
  const normalizedValue = typeof cleanedValue === 'string' ? cleanedValue.toLowerCase() : cleanedValue;

  let valueLabel = cleanedValue;

  if (typeof fieldInfo === 'object' && fieldInfo.values) {
    // Si el campo tiene un map de valores, retornar el label correspondiente
    valueLabel = fieldInfo.values[normalizedValue] ?? cleanedValue;
  } else if (typeof fieldInfo === 'object' && fieldInfo.formatter) {
    // Si el campo tiene un formatter, retornar el label formateado
    valueLabel = fieldInfo.formatter(cleanedValue);
  } else if (GENERAL_VALUE_LABELS_MAP.has(normalizedValue)) {
    // Retorna el label correspondiente si el valor está definido en GENERAL_VALUE_LABELS_MAP
    valueLabel = GENERAL_VALUE_LABELS_MAP.get(normalizedValue);
  }

  if (valueLabel === null || valueLabel === undefined || valueLabel === '') {
    return EMPTY_DISPLAY_VALUE;
  }

  return valueLabel;
};

const changesFromDto = (entity, dto) => {
  return Object.entries(dto).reduce((acc, [field, value]) => {
    const fieldLabel = getFieldLabel(entity, field);

    if (fieldLabel == null) return acc;

    acc[fieldLabel] = {
      previous: getValueLabel(entity, field, value.previous),
      current: getValueLabel(entity, field, value.current),
    };
    return acc;
  }, {});
};

export const changeLogFromDto = (dto) => ({
  brand: {
    id: dto.brand.id,
    externalId: dto.brand.external_id,
    name: dto.brand.name,
  },
  user: {
    id: dto.user.id,
    externalId: dto.user.external_id,
    username: dto.user.username,
    firstName: dto.user.first_name,
    lastName: dto.user.last_name,
    email: dto.user.email,
    brandUserId: dto.user.brand_user_id,
    profile: {
      id: dto.user.profile.id,
      name: dto.user.profile.name,
    },
    advisor: dto.user.advisor && {
      id: dto.user.advisor.id,
      name: dto.user.advisor.name,
    },
  },
  action: {
    type: dto.action.type, // 'create' | 'update' | 'delete' | 'requote'
    summary: dto.action.summary,
    triggerSource: dto.action.trigger_source, // 'manual' | 'system'
    origin: {
      trace: dto.action.origin.trace,
      entity: dto.action.origin.entity && {
        id: dto.action.origin.entity.id,
        name: dto.action.origin.entity.name,
        summary: dto.action.origin.entity.summary,
      },
    },
    changes: changesFromDto(dto.entity.name, dto.action.changes),
  },
  entity: {
    id: dto.entity.id,
    name: dto.entity.name, // 'client' | 'vehicle' | 'quotation'
    summary: dto.entity.summary,
  },
  metadata: {
    timestamp: dto.metadata.timestamp,
    ipAddress: dto.metadata.ip_address,
    version: dto.metadata.version,
  },
});

export const filtersToDto = (filters) => {
  // Se ajustan las fechas para incluir todo el rango del día y se convierten a UTC
  const timestampStart = filters.timestampStart ? dayjs(filters.timestampStart).startOf('day').utc().format() : undefined;
  const timestampEnd = filters.timestampEnd ? dayjs(filters.timestampEnd).endOf('day').utc().format() : undefined;

  return {
    users: filters.users,
    action_types: filters.actionTypes,
    entity_name: filters.entity,
    entity_id: filters.entityId,
    timestamp_start: timestampStart,
    timestamp_end: timestampEnd,
  }
};
